import type { SeededRandom } from '../../seeded-random';
import type { GenerateFillLayoutOptions, MapGenTileCoord, TileLayoutResult } from '../tile-layout-types';

import type { FillLayoutContext } from './fill-layout-context';
import { createFillLayoutContext, topUpAndFinalize } from './fill-layout-context';
import {
  canCommitCells,
  canPlaceGuidedTile,
  cellsFitWithForeignGap,
  commitCells,
  DIRECTIONS_8,
  type GridStep,
  shuffleDirections,
  tileKey,
  turnDirection
} from './wall-line-shared';

/**
 * Gap-aware wall lines that grow from free starts and refuse to crowd foreign fill tiles.
 * More organic than single-right-angle / line stamps; good when rigid L walls feel too regular.
 */
export function generateGapAwareOrganicWallsLayout(options: GenerateFillLayoutOptions): TileLayoutResult {
  const ctx = createFillLayoutContext(options);
  const primaryFilled = placeGuidedWallLines(ctx);
  return topUpAndFinalize(ctx, 'gapAwareOrganicWalls', primaryFilled);
}

function placeGuidedWallLines(ctx: FillLayoutContext): number {
  const { grid, width, height, budget, gapTiles, minPlacementSizeTiles, maxPlacementSizeTiles, fillTileValue, rand } = ctx;
  const wallCount = Math.max(8, Math.floor((width * height * ctx.coverageCap) / 100));
  let filled = 0;

  for (let i = 0; i < wallCount; i++) {
    const complexityBias = 1 - (i / Math.max(1, wallCount));
    const turnChance = 0.22 + (0.30 * complexityBias);

    for (let length = maxPlacementSizeTiles; length >= minPlacementSizeTiles; length--) {
      const cells = planGuidedWallLine(grid, width, height, length, turnChance, gapTiles, fillTileValue, rand);
      if (cells.length < minPlacementSizeTiles) {
        continue;
      }

      if (!cellsFitWithForeignGap(grid, width, height, cells, gapTiles, fillTileValue)) {
        continue;
      }

      if (!canCommitCells(grid, width, height, cells, budget, fillTileValue)) {
        continue;
      }
      filled += commitCells(grid, width, height, cells, fillTileValue);
      break;
    }
  }

  return filled;
}

function planGuidedWallLine(
  grid: Uint8Array,
  width: number,
  height: number,
  targetLength: number,
  turnChance: number,
  gapTiles: number,
  fillValue: number,
  rand: SeededRandom
): MapGenTileCoord[] {
  const start = findGuidedStart(grid, width, height, gapTiles, fillValue, rand);
  if (!start) {
    return [];
  }

  const cells: MapGenTileCoord[] = [];
  const ownCells = new Set<string>();
  let col = start.col;
  let row = start.row;
  let dir = DIRECTIONS_8[Math.floor(rand() * DIRECTIONS_8.length)];

  for (let step = 0; step < targetLength; step++) {
    if (!canPlaceGuidedTile(grid, width, height, col, row, ownCells, gapTiles, fillValue)) {
      break;
    }

    cells.push({ col, row });
    ownCells.add(tileKey(col, row));

    if (step > 1 && rand() < turnChance) {
      dir = turnDirection(dir, DIRECTIONS_8, rand);
    }

    const next = pickNextGuidedStep(grid, width, height, col, row, dir, ownCells, gapTiles, fillValue, rand);
    if (!next) {
      break;
    }
    col = next.col;
    row = next.row;
    dir = next.dir;
  }

  return cells;
}

function findGuidedStart(
  grid: Uint8Array,
  width: number,
  height: number,
  gapTiles: number,
  fillValue: number,
  rand: SeededRandom
): MapGenTileCoord | null {
  for (let attempt = 0; attempt < 80; attempt++) {
    const col = Math.floor(rand() * width);
    const row = Math.floor(rand() * height);
    if (canPlaceGuidedTile(grid, width, height, col, row, new Set<string>(), gapTiles, fillValue)) {
      return { col, row };
    }
  }
  return null;
}

function pickNextGuidedStep(
  grid: Uint8Array,
  width: number,
  height: number,
  col: number,
  row: number,
  preferred: GridStep,
  ownCells: Set<string>,
  gapTiles: number,
  fillValue: number,
  rand: SeededRandom
): { col: number; row: number; dir: GridStep } | null {
  const candidates = [preferred, ...shuffleDirections(DIRECTIONS_8, rand)]
    .filter((dir, index, arr) => arr.findIndex((other) => other.dc === dir.dc && other.dr === dir.dr) === index);

  for (const dir of candidates) {
    const nextCol = col + dir.dc;
    const nextRow = row + dir.dr;
    if (canPlaceGuidedTile(grid, width, height, nextCol, nextRow, ownCells, gapTiles, fillValue)) {
      return { col: nextCol, row: nextRow, dir };
    }
  }
  return null;
}
